// O impacto da Reforma Tributária (EC 132/2023) sobre uma empresa do Simples
// Nacional. Puro, com prova própria (`app/tests/simples-nacional.mjs`).
//
// ---------------------------------------------------------------------------
// O QUE ESTA CONTA COMPARA — e o que ela NÃO diz
// ---------------------------------------------------------------------------
// O IVA Dual substitui CINCO tributos sobre o consumo: a CBS entra no lugar de
// PIS e Cofins (federal), o IBS no lugar de ICMS e ISS (estadual e municipal).
// IRPJ, CSLL, CPP e IPI **não** são tocados.
//
// Então a comparação honesta é entre os quatro substituídos — e só entre eles.
// Somar o DAS inteiro de um lado e CBS+IBS do outro compararia coisas
// diferentes e daria um "aumento" que não existe.
//
// E o que a conta NÃO diz, mas a tela precisa dizer: a empresa do Simples PODE
// continuar recolhendo CBS e IBS dentro do DAS, pelas alíquotas do próprio
// Simples. Sair do DAS é uma OPÇÃO, que vale a pena quando os clientes são
// pessoas jurídicas que aproveitam o crédito. Esta simulação mostra o cenário
// "fora do DAS"; apresentá-la como destino inevitável seria assustar quem não
// precisa mudar nada.
//
// ---------------------------------------------------------------------------
// AS ALÍQUOTAS NÃO SÃO LEI AINDA, E VÊM DO BANCO
// ---------------------------------------------------------------------------
// CBS 8,8% e IBS 17,7% são a alíquota de REFERÊNCIA estimada (PLP 68/2024;
// somadas dão os 26,5% que o governo divulgou). O valor final sai de lei
// complementar e de resolução do Senado, e a transição vai de 2026 a 2033.
//
// Número que muda por ato do Legislativo dentro de `.tsx` é ferramenta que
// expira sozinha — a regra do corte da 000108. Por isso os dois vêm de
// `rs_parametros`, com a REFERÊNCIA junto, e a tela os mostra ao lado do número:
// número certo sem procedência ensina a confiar em número velho.

import { centavos } from "./folhaDp";
import type { Reparticao } from "./simplesNacional";

export interface EntradaReforma {
  /** A receita do mês — a base do CBS e do IBS. */
  receitaDoMes: number;
  /** O DAS do mês, já calculado por `calcularAliquota`. */
  das: number;
  /** A repartição do par (anexo, faixa) — em percentual do DAS. */
  reparticao: Reparticao;
  /** Alíquota da CBS em percentual. */
  cbsPercentual: number;
  /** Alíquota do IBS em percentual. */
  ibsPercentual: number;
  /** Créditos de CBS sobre as compras do mês, em reais. */
  creditosCbs: number;
  /** Créditos de IBS sobre as compras do mês, em reais. */
  creditosIbs: number;
}

export interface ResultadoReforma {
  /** PIS + Cofins que hoje estão dentro do DAS. */
  pisCofinsHoje: number;
  /** ICMS + ISS que hoje estão dentro do DAS. */
  icmsIssHoje: number;
  /** O total substituído — a única parte do DAS que a reforma alcança. */
  substituidoHoje: number;

  cbs: number;
  ibs: number;
  creditosCbs: number;
  creditosIbs: number;
  /** CBS + IBS, já descontados os créditos. Nunca negativo. */
  totalReforma: number;

  /** Reforma − atual. Positivo é aumento. */
  diferenca: number;
  /**
   * A diferença em percentual do que é substituído hoje. NULO quando não há
   * nada substituído — dividir por zero daria Infinity, e a origem imprimia
   * `(diferenca / (a + b || 1)) * 100`, que é pior: com base zero ela mostrava
   * um percentual construído sobre um denominador inventado.
   */
  diferencaPercentual: number | null;
  /**
   * True quando não há PIS/Cofins nem ICMS/ISS no DAS.
   *
   * Medido: NENHUMA das 30 faixas fica sem os quatro — todas têm PIS e Cofins,
   * inclusive a 6ª do Anexo IV, onde só o ISS sai. O que zera a base é o MÊS sem
   * faturamento, e aí comparar não faz sentido: a tela diz isso em vez de
   * mostrar um percentual sobre um denominador inventado.
   */
  semBaseDeComparacao: boolean;
}

export function calcularReforma(e: EntradaReforma): ResultadoReforma {
  const receita = Math.max(0, e.receitaDoMes);
  const das = Math.max(0, e.das);

  const pisCofinsHoje = centavos(das * ((e.reparticao.PIS + e.reparticao.COFINS) / 100));
  const icmsIssHoje = centavos(das * ((e.reparticao.ICMS + e.reparticao.ISS) / 100));
  const substituidoHoje = centavos(pisCofinsHoje + icmsIssHoje);

  const cbs = centavos(receita * (Math.max(0, e.cbsPercentual) / 100));
  const ibs = centavos(receita * (Math.max(0, e.ibsPercentual) / 100));
  const creditosCbs = Math.max(0, e.creditosCbs);
  const creditosIbs = Math.max(0, e.creditosIbs);

  // Crédito maior que o débito NÃO vira imposto negativo: ele fica como saldo
  // credor para o mês seguinte. A origem somava `(cbs - credito) + (ibs -
  // credito)` sem piso, então um mês de compras grandes mostrava a empresa
  // "recebendo" imposto — e o crédito de CBS ainda compensava débito de IBS,
  // que são tributos de esferas diferentes e não se cruzam.
  const cbsAPagar = Math.max(0, centavos(cbs - creditosCbs));
  const ibsAPagar = Math.max(0, centavos(ibs - creditosIbs));
  const totalReforma = centavos(cbsAPagar + ibsAPagar);

  const diferenca = centavos(totalReforma - substituidoHoje);
  const semBaseDeComparacao = substituidoHoje <= 0;

  return {
    pisCofinsHoje,
    icmsIssHoje,
    substituidoHoje,
    cbs,
    ibs,
    creditosCbs,
    creditosIbs,
    totalReforma,
    diferenca,
    diferencaPercentual: semBaseDeComparacao
      ? null
      : Math.round((diferenca / substituidoHoje) * 10000) / 100,
    semBaseDeComparacao,
  };
}
